import {
  Typography,
  Input,
  HorizontalDivider,
  Button,
  Notification,
  Icon
} from '@smartgift/frontend-toolkit';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import uniq from 'lodash/uniq';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { GiftCollectionSizeModal } from '@hero/components/modals';
import { COLLECTIONS_TAB, MAX_FEATURE_NAME_LENGTH } from '@hero/constants';
import { PageLayout } from '@hero/layouts';
import { DetailItem, ProductVerticalList, VerticalListHeader } from '@hero/molecules';
import { ChooseFeatureTypeModal, EditableProductsModal } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import {
  useDeleteCollection,
  useDuplicateCollection,
  useGetCollection,
  useGetCollectionProducts,
  useUpdateCollection
} from '@hero/services/product-service/collection/hooks';
import { useNotification } from '@hero/store/hooks';
import { getFormattedPrice, getNameFeatureFormData } from '@hero/utils';
import { getNameSchema } from '@hero/validations';

import * as S from './style';

export default function CollectionsDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { collectionId } = useParams();

  const { mutateAsync: updateCollection } = useUpdateCollection();
  const { mutateAsync: deleteCollection, isLoading: isLoadingDeleteCollection } =
    useDeleteCollection();
  const { mutateAsync: duplicateCollection } = useDuplicateCollection();
  const { data: collection, isFetching: isFetchingCollection } = useGetCollection({
    collectionId
  });

  const { showNotification, showSuccess } = useNotification();

  const collectionType = collection?.isPublic ? COLLECTIONS_TAB.featured : COLLECTIONS_TAB.saved;

  const { data: collectionProducts, isFetching: isFetchingProducts } =
    useGetCollectionProducts(collectionId);

  const [showProductModal, setShowProductModal] = React.useState(false);
  const [showSizeWarnOpen, setShowSizeWarnOpen] = React.useState(false);
  const [name, setName] = React.useState();

  const unavailableProducts = useMemo(() => {
    return uniq(collection?.products)?.filter(
      p =>
        !collectionProducts?.map(product => product.merchantProductId).includes(p.merchantProductId)
    );
  }, [collectionProducts, collection?.products]);

  const selectedProducts = useMemo(() => {
    return collectionProducts;
  }, [collectionProducts, collection?.products]);

  const { minPrice, maxPrice } = useMemo(() => {
    const selectedSkus = selectedProducts?.reduce((r, p) => r.concat(p.skus), []);

    return {
      maxPrice: maxBy(selectedSkus, 'price')?.price,
      minPrice: minBy(selectedSkus, 'price')?.price
    };
  }, [selectedProducts]);

  const handleRemoveProduct = async (merchantProductId, product) => {
    const products = collectionProducts.filter(
      item => item.merchantProductId !== merchantProductId
    );

    await updateCollection({
      params: { collectionId },
      body: {
        products: products.map(product => ({
          id: product.merchantProductId,
          merchantProductId: product.merchantProductId,
          source: product?.source || undefined,
          images: product?.images.map(i => ({ id: i.id, url: i.url })),
          name: product?.name,
          brand: product?.brand,
          skus: product.skus.map(sku => ({
            code: sku.code,
            price: sku.price
          }))
        }))
      }
    });

    showSuccess({
      type: 'toast',
      message: t('successfullyRemovedMessage', { name: product?.name })
    });
  };

  const handleUpdateProducts = products => {
    updateCollection({
      params: { collectionId },
      body: {
        products: products.map(product => ({
          id: product.merchantProductId,
          merchantProductId: product.merchantProductId,
          source: product?.source || undefined,
          images: product?.images.map(i => ({ id: i.id, url: i.url })),
          name: product?.name,
          brand: product?.brand,

          skus: product.skus.map(sku => ({
            code: sku.code,
            price: sku.price
          }))
        }))
      }
    });
  };

  useEffect(() => {
    if (collection?.name) {
      setName(collection.name);
    }
  }, [collection]);

  const isEditable = collectionType === COLLECTIONS_TAB.saved;

  const GIFT_ALERT_LIMIT = 12;
  const hasGiftAlert = isEditable && collectionProducts?.length >= GIFT_ALERT_LIMIT;

  const [chooseFeatureModal, setChooseFeatureModal] = useState(false);
  const [initialProducts, setInitialProducts] = useState([]);

  const handleSendWithFeature = async productsData => {
    setChooseFeatureModal(true);
    const initialProducts = collectionProducts.map(doc => {
      const p = productsData.find(p => p.merchantProductId === doc.merchantProductId);
      doc.skus = doc.skus.filter(sku => p.skus.find(s => s.code === sku.code));

      return doc;
    });
    setInitialProducts(initialProducts);
  };

  return (
    <PageLayout
      header={{
        title: t('typeNameDetails', { name: t('collection') }),
        showBackButton: true,
        backButton: {
          onClick: () => {
            navigate(ROUTE_PATHS.COLLECTIONS());
          }
        }
      }}
      navigation={{
        leftButton: {
          children: t('back'),
          onClick: () => navigate(ROUTE_PATHS.COLLECTIONS())
        }
      }}
    >
      {hasGiftAlert && (
        <Notification
          status="info"
          title={t('giftCollectionSize')}
          content={
            <Typography
              style={{
                display: 'inline-flex',
                gap: 4,
                alignItems: 'center',
                flexWrap: 'wrap',
                width: '100%'
              }}
              variant="body2"
              emphasis="weak"
              as="div"
            >
              <Trans
                i18nKey="giftCollectionSizeDescription"
                components={{
                  b: <b />,
                  a: (
                    <Button
                      onClick={() => {
                        setShowSizeWarnOpen(true);
                      }}
                      variant="link"
                      color="neutral"
                      emphasis="800"
                    />
                  )
                }}
                values={{
                  itemCount: collectionProducts?.pagination?.total
                }}
              />
            </Typography>
          }
        />
      )}
      {!selectedProducts?.length && (
        <Notification
          status="warning"
          content={
            <Typography
              variant="body2"
              emphasis="weak"
              style={{
                display: 'inline-flex',
                gap: 4,
                alignItems: 'center',
                flexWrap: 'wrap',
                width: '100%'
              }}
            >
              <Trans
                i18nKey="toContinueEditGiftCollection"
                components={{
                  button: (
                    <Button
                      onClick={() => {
                        setShowProductModal(true);
                      }}
                      variant="link"
                      color="neutral"
                      emphasis="800"
                    />
                  )
                }}
              />
            </Typography>
          }
        />
      )}
      <S.CollectionsDetail>
        <S.LeftContent>
          <Typography variant="body1Bold">{t('collection')}</Typography>
          <DetailItem
            editable={!collection?.isPublic}
            bodyStyle={{ alignItems: 'center', margin: '16px 0' }}
            title={`${t('collectionName')}:`}
            withBorder
            closeWithoutSave={() => {
              setName(collection?.name);
            }}
            saveDetail={() => {
              updateCollection({
                params: { collectionId },
                body: {
                  name
                }
              });
            }}
            editInput={() => (
              <Input
                value={name}
                inputProps={{
                  onChange: e => setName(e.target.value),
                  maxLength: MAX_FEATURE_NAME_LENGTH
                }}
                formItem={{
                  label: t('collectionNameDesc'),
                  name: 'collection_name',
                  validations: {
                    required: {
                      required: true
                    }
                  }
                }}
              />
            )}
          >
            {name}
          </DetailItem>

          <VerticalListHeader
            updateProducts={handleUpdateProducts}
            setShowProductModal={setShowProductModal}
            isDetailItemEditable={!collection?.isPublic}
            products={selectedProducts}
            isFetching={isFetchingCollection || isFetchingProducts}
            noItemsFound={selectedProducts?.length === 0}
            originalProductLength={collection?.products?.length}
            hasAlert={false}
          />

          <ProductVerticalList
            featureName={t('collection')}
            isFetching={isFetchingProducts}
            products={{ docs: collectionProducts }}
            isProductsRemovable={!collection?.isPublic}
            onRemoveProduct={handleRemoveProduct}
            unavailableProducts={unavailableProducts}
            updateProducts={handleUpdateProducts}
            creationProducts={collection?.products?.map(product => {
              return { ...product, merchantProductId: product?.merchantProductId };
            })}
          />

          <HorizontalDivider $margin="16px 0" />

          <Typography variant="body2SemiBold" emphasis="weak">
            {t('verticalListSubTitle')}
          </Typography>

          {showProductModal && collection && selectedProducts && (
            <EditableProductsModal
              isModalOpen={showProductModal}
              onRequestClose={() => setShowProductModal(false)}
              selectedProducts={selectedProducts}
              updateProducts={handleUpdateProducts}
              featureType="collection"
              isDraft
              init
            />
          )}
        </S.LeftContent>

        <S.PriceSummary>
          <Typography variant="body1Bold">{t('priceSummary')}</Typography>
          <S.PriceSummaryInfo>
            <S.RowTitle>
              <S.ItemLabel>{t('itemPriceRange')}:</S.ItemLabel>
              <S.ItemHelpText>({t('itemPriceRangeHelpText')})</S.ItemHelpText>
            </S.RowTitle>
            <S.PriceLabel>{getFormattedPrice({ min: minPrice, max: maxPrice })}</S.PriceLabel>
          </S.PriceSummaryInfo>

          <Button
            onClick={() => {
              handleSendWithFeature(collection?.products);
            }}
            block
            disabled={!selectedProducts?.length}
          >
            {t('quickSend')}
          </Button>
          <Typography
            style={{
              display: 'inline-flex',
              gap: 4,
              alignSelf: 'center',
              flexWrap: 'wrap',
              width: '100%',
              justifyContent: 'center',
              marginTop: 8
            }}
            variant="body2"
            emphasis="weak"
            as="div"
          >
            <Trans
              i18nKey={isEditable ? 'youCanDuplicateOrDeleteFeature' : 'youCanDuplicateFeature'}
              components={{
                button1: (
                  <Button
                    onClick={() => {
                      showNotification({
                        header: t('nameYourFeature', { name: t('collection').toLocaleLowerCase() }),
                        notificationImage: <Icon name="collections" size="xxl" />,
                        formData: getNameFeatureFormData({
                          label: t('featureName', { featureType: t('collection') }),
                          value: t('copyText', { copyText: collection.name })
                        }),
                        formValidationSchema: getNameSchema(),
                        secondaryButtonProps: { children: t('cancel') },
                        onSubmit: async ({ formValues }) => {
                          const response = await duplicateCollection({
                            ...formValues,
                            products: collection?.products
                          });

                          navigate(ROUTE_PATHS.COLLECTIONS_DETAIL(response.id));
                        },
                        primaryButtonProps: {
                          endIcon: <Icon name="next" />,
                          children: t('createAndReview')
                        }
                      });
                    }}
                    variant="link"
                    color="neutral"
                    emphasis="800"
                  />
                ),
                button2: (
                  <Button
                    onClick={async () => {
                      showNotification({
                        formStyle: {
                          padding: '0 50px'
                        },
                        primaryButtonProps: { color: 'error', children: t('delete') },
                        notificationImage: (
                          <Icon name="error" size="xxl" color="var(--color-error-default)" />
                        ),
                        title: t('areYouSureYouWantDelete', {
                          type: t('collection').toLocaleLowerCase()
                        }),
                        secondaryButtonProps: { children: t('keepCollection') },
                        onSubmit: async () => {
                          const collectionName = collection?.name;
                          try {
                            await deleteCollection(collectionId);
                          } finally {
                            showSuccess({
                              type: 'toast',
                              message: t('successfullyRemovedMessage', { name: collectionName })
                            });
                            navigate(ROUTE_PATHS.COLLECTIONS());
                          }
                        }
                      });
                    }}
                    variant="link"
                    color="neutral"
                    emphasis="800"
                    loading={isLoadingDeleteCollection}
                  />
                )
              }}
              values={{
                featureType: t('collection').toLowerCase()
              }}
            />
          </Typography>
        </S.PriceSummary>
      </S.CollectionsDetail>

      <GiftCollectionSizeModal
        isOpen={showSizeWarnOpen}
        setIsOpen={setShowSizeWarnOpen}
        primaryButtonProps={{
          children: t('editGiftCollection'),
          onClick: () => {
            setShowSizeWarnOpen(false);
            setShowProductModal(true);
          }
        }}
      />

      <ChooseFeatureTypeModal
        initialProducts={initialProducts}
        setInitialProducts={setInitialProducts}
        isOpen={chooseFeatureModal}
        redirectToGiftStep
        onClose={() => {
          setChooseFeatureModal(false);
        }}
      />
    </PageLayout>
  );
}
